import Button from '@rio-cloud/rio-uikit/Button';
import ClearableInput from '@rio-cloud/rio-uikit/ClearableInput';
import { FunctionComponent, SubmitEvent, useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { runInBackground } from '../../../configuration/setup/backgroundActions';
import { Environment, SelectedContext } from './dataQuery.types';
import { ContextSelection } from './ContextSelection';
import { EnvironmentSelection } from './EnvironmentSelection';

type QueryFormProps = {
    isProcessing: boolean;
    processQuery: (query: string, selectedContext: SelectedContext, environment: Environment) => Promise<void>;
};

const MAX_QUERY_LENGTH = 1000;

// eslint-disable-next-line max-lines-per-function
export const QueryForm: FunctionComponent<QueryFormProps> = ({ isProcessing, processQuery }) => {
    const inputId = useId();
    const intl = useIntl();
    const [query, setQuery] = useState('');
    const [selectedContext, setSelectedContext] = useState<SelectedContext | undefined>();
    const [environment, setEnvironment] = useState(Environment.PROD);

    const disableInput = isProcessing || selectedContext === undefined || environment === undefined;
    const disableSubmit = disableInput || query.length === 0;

    const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!disableSubmit && selectedContext !== undefined && environment !== undefined) {
            runInBackground(processQuery(query, selectedContext, environment));
        }
    };

    return (
        <div className="width-100pct display-flex justify-content-center">
            <form className="width-800-md width-600-sm width-100pct-ls width-100pct-xs" onSubmit={handleSubmit}>
                <div className="form-group">
                    <div className="callout callout-info">
                        <div className="text-color-info text-medium text-size-20 line-height-125rel margin-bottom-10">
                            <FormattedMessage id="outboundOrderBook.inspector.dataquery.disclaimerTitle" />
                        </div>
                        <p>
                            <FormattedMessage
                                id="outboundOrderBook.inspector.dataquery.disclaimerContent"
                                values={{
                                    newline: <br key="newline" />,
                                    b: (chunks) => <strong>{chunks}</strong>,
                                }}
                            />
                        </p>
                        <ul>
                            <li>
                                <FormattedMessage id="outboundOrderBook.inspector.dataquery.disclaimerContentItem1" />
                            </li>
                            <li>
                                <FormattedMessage id="outboundOrderBook.inspector.dataquery.disclaimerContentItem2" />
                            </li>
                            <li>
                                <FormattedMessage id="outboundOrderBook.inspector.dataquery.disclaimerContentItem3" />
                            </li>
                            <li>
                                <FormattedMessage id="outboundOrderBook.inspector.dataquery.disclaimerContentItem4" />
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="form-group">
                    <div className="display-flex align-items-start">
                        <div className="width-50pct padding-right-5">
                            <ContextSelection
                                disabled={isProcessing}
                                selectedContext={selectedContext}
                                setSelectedContext={setSelectedContext}
                            />
                        </div>
                        <div className="width-50pct">
                            <EnvironmentSelection
                                disabled={isProcessing}
                                environment={environment}
                                setEnvironment={setEnvironment}
                            />
                        </div>
                    </div>
                    <label htmlFor={inputId}>
                        <FormattedMessage id="outboundOrderBook.inspector.dataquery.form.query.label" />
                    </label>
                    <div className="input-group">
                        <span className="input-group-addon">
                            <span className="rioglyph rioglyph-search" />
                        </span>
                        <ClearableInput
                            id={inputId}
                            maxLength={MAX_QUERY_LENGTH}
                            placeholder={intl.formatMessage({
                                id: 'outboundOrderBook.inspector.dataquery.form.query.placeholder',
                            })}
                            value={query}
                            onChange={setQuery}
                            disabled={disableInput}
                            autoComplete="off"
                        />
                        <span className="input-group-btn">
                            <Button disabled={disableSubmit} type="submit" bsStyle={Button.PRIMARY}>
                                <FormattedMessage id="outboundOrderBook.inspector.dataquery.form.submitQuery" />
                            </Button>
                        </span>
                    </div>
                </div>
            </form>
        </div>
    );
};
